import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CreatePatientReportDocumentForm } from '@modules/patient/patient-report-document/create-patient-report-document-form';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DocumentTemplateType } from '@pixacare/domain';
import { DocumentTemplateResponse, FlattenedSequence, PatientReportRequest } from '@pixacare/dtos';
import { PatientHttpService } from '@services/http/patient.http.service';
import { ReportApiService } from '@services/http/report.api.service';
import { PatientReportExportMode } from '@shared/event-tracker/models/patient-report-export-mode.enum';
import { EventTrackerService } from '@shared/event-tracker/services/event-tracker.service';
import { ObjectFormControl } from '@shared/models/object-form-control';
import { PdfResponse } from '@shared/models/pdf-response';
import { toRequestResult } from '@shared/request-state/adapter/to-request-result';
import { RequestResult } from '@shared/request-state/models/request-result';
import { withRequestResult } from '@shared/request-state/rx-pipes/with-request-result.pipe';
import { getInitialRequestState } from '@shared/request-state/utils/get-initial-request-state';
import { Observable, shareReplay, startWith, switchMap } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, tap, withLatestFrom } from 'rxjs/operators';

@UntilDestroy()
@Injectable()
export class PatientReportDocumentService {
  private readonly patientService = inject(PatientHttpService);
  private readonly eventTrackerService = inject(EventTrackerService);
  private readonly reportService = inject(ReportApiService);

  private clientCode: string;
  private patientId: number;

  readonly templates$: Observable<DocumentTemplateResponse[]> = this.reportService.getTemplates(
    DocumentTemplateType.PATIENT,
  );

  readonly form = new FormGroup<ObjectFormControl<CreatePatientReportDocumentForm>>({
    departmentIds: new FormControl<number[]>([]),
    templateId: new FormControl<number>(null, [Validators.required]),
    sequences: new FormControl<FlattenedSequence[]>([]),
  });

  readonly patientReportRequest$ = this.form.valueChanges.pipe(
    filter(() => this.form.valid),
    map((values) => this.toPatientReportRequest(values)),
    debounceTime(300),
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
    shareReplay(1),
  );

  readonly trackEventData$ = this.patientReportRequest$.pipe(
    withLatestFrom(this.templates$),
    map(([formValues, templates]) => {
      return {
        templateName: templates.find((template) => template.id === formValues.templateId)?.name || '',
        isCollaborative: formValues.departmentIds.length > 0,
        patientId: this.patientId,
      };
    }),
  );

  init(clientCode: string, patientId: number, departmentIds?: number[]): void {
    this.clientCode = clientCode;
    this.patientId = patientId;

    if (departmentIds?.length) {
      this.form.controls.departmentIds.setValue(departmentIds, { emitEvent: false });
    }

    this.form.controls.departmentIds.valueChanges
      .pipe(
        tap(() => this.form.controls.sequences.setValue([])),
        untilDestroyed(this),
      )
      .subscribe();
  }

  readonly pdfPreview$: Observable<RequestResult<PdfResponse>> = this.patientReportRequest$.pipe(
    tap(() => this.trackPatientReportEvent()),
    switchMap((formValues) => {
      return this.patientService.report(this.clientCode, this.patientId, formValues).pipe(withRequestResult());
    }),
    startWith(toRequestResult(getInitialRequestState(null))),
    shareReplay(1),
  );

  trackPatientReportExportEvent(mode: PatientReportExportMode): void {
    this.trackEventData$
      .pipe(first())
      .subscribe((event) => this.eventTrackerService.trackPatientReportExport({ ...event, mode }));
  }

  private trackPatientReportEvent(): void {
    this.trackEventData$.pipe(first()).subscribe((event) => this.eventTrackerService.trackPatientReport(event));
  }

  private toPatientReportRequest(form: Partial<CreatePatientReportDocumentForm>): PatientReportRequest {
    return {
      templateId: form.templateId,
      departmentIds: form.departmentIds || [],
      sequenceIds: form.sequences?.map((sequence) => sequence.sequenceInstance.id) || [],
    };
  }
}
